import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { TelemetryService } from './telemetry.service';
import { TelemetryEvent } from '@weavix/telemetry';

export * from '@weavix/models/src/analytics/analytics';

declare global {
    interface Window {
        analytics: SegmentAnalytics.AnalyticsJS;
    }
}

const source = environment.analyticsSource;
const version = environment.version;
const USE_ANALYTICS_KEY = 'use-analytics';

function trackIt() {
    return !!localStorage.getItem(USE_ANALYTICS_KEY);
}

export interface IdentifyTraits {
    email: string;
    name: string;
    accountId: string;
}

export enum StProperty {
    accountId = 'accountId',
    action = 'action',
    channelId = 'channelId',
    channelType = 'channelType',
    component = 'component',
    count = 'count',
    destinationLanguages = 'destinationLanguages',
    dimensions = 'dimensions',
    duration = 'duration',
    endpoint = 'endpoint',
    errorData = 'errorData',
    facilityId = 'facilityId',
    id = 'id',
    memberCount = 'memberCount',
    method = 'method',
    object = 'object',
    page = 'page',
    query = 'query',
    source = 'source',
    sourceLanguage = 'sourceLanguage',
    sourceLocale = 'sourceLocale',
    sourceDialect = 'sourceDialect',
    type = 'type',
    unit = 'unit',
    url = 'url',
    version = 'version',
    visible = 'visible',
}

export type StProperties = { [key in StProperty]?: any };


const allowedKeys = new RegExp((Object.keys(StProperty).join('|')), 'i');
function redact(obj: any) {
    if (Array.isArray(obj)) {
        const result = [];
        for (let i = 0; i < obj.length; i++) {
            const redacted = redact(obj[i]);
            result.push(redacted === undefined ? !!obj[i] : redacted);
        }
        return result;
    } else if (obj && typeof obj === 'object') {
        const result = {};
        Object.keys(obj).forEach(key => {
            const val = obj[key];
            if (allowedKeys.test(key)) {
                result[key] = val;
            } else {
                const redacted = redact(val);
                result[key] = redacted === undefined ? !!val : redacted;
            }
        });
        return result;
    }
    return undefined;
}

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {

    private static readonly USE_ANALYTICS_KEY = 'use-analytics';

    static telemetryService: TelemetryService;

    constructor(telemetryService: TelemetryService) {
        AnalyticsService.telemetryService = telemetryService;
    }

    static trackIt() {
        return trackIt();
    }

    static start() {
        localStorage.setItem(this.USE_ANALYTICS_KEY, 'true');
    }

    static stop() {
        localStorage.removeItem(this.USE_ANALYTICS_KEY);
    }

    static identify(userId: string, traits: IdentifyTraits): void {
        if (trackIt()) {
            window.analytics.identify(userId, { ...traits });
            AnalyticsService.telemetryService.setTraits({ userId, ...traits });
        }
    }

    static track(object: string, action: string, component: string, data: StProperties = {}): void {
        try {
            if (trackIt()) {
                const allProps: StProperties = { object, action, version, source, component, ...redact(data) };
                window.analytics.track(`${object} : ${action}`, allProps);
                if (TelemetryEvent[object + action]) AnalyticsService.telemetryService.sendTelemetry(object + action as TelemetryEvent, allProps);
            }
        } catch (e) {
            console.error(e);
        }
    }

    static error(method: string, endpoint: string, component: string, errorData: StProperties = {}): void {
        try {
            if (trackIt()) {
                const allProps: StProperties = { method, endpoint, version, source, component, ...redact(errorData) };
                window.analytics.track(`HTTP ERROR : ${method}`, allProps);
            }
        } catch (e) {
            console.error(e);
        }
    }

    static page(page: string, url?: string, facilityId?: string): void {
        try {
            if (trackIt()) {
                const allProps: StProperties = { action: 'Viewed', page, source, version, url, facilityId };
                console.log(`TRACK ${page} : Viewed`, allProps);
                window.analytics.track(`${page} : Viewed`, allProps);
                AnalyticsService.telemetryService.sendTelemetry(`${source.replace('-', '')}PageViewed` as TelemetryEvent, allProps);
            }
        } catch (e) {
            console.error(e);
        }
    }
}
